import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useCreateProject } from '../../hooks/useProjects';
import { SaveIndicator } from '../SaveIndicator';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { AiSettingsModal } from '../Settings/AiSettingsModal';

interface MainLayoutProps {
  children: React.ReactNode;
}

const navGroups = [
  {
    title: 'Panoramica',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: '📊' },
      { path: '/immobile', label: 'Scheda Immobile', icon: '🏠' },
    ]
  },
  {
    title: 'Acquisto',
    items: [
      { path: '/finanziamento', label: 'Finanziamento', icon: '💰' },
      { path: '/notaio', label: 'Notaio', icon: '📝' },
    ]
  },
  {
    title: 'Lavori',
    items: [
      { path: '/preventivi', label: 'Preventivi', icon: '📋' },
      { path: '/gantt', label: 'Gantt', icon: '📅' },
    ]
  },
  {
    title: 'Gestione',
    items: [
      { path: '/task', label: 'Task', icon: '✅' },
      { path: '/rilievi', label: 'Rilievi', icon: '📷' },
      { path: '/resoconto', label: 'Resoconto', icon: '🧾' },
      { path: '/flussi-cassa', label: 'Flussi Cassa', icon: '📈' },
      { path: '/scadenze', label: 'Scadenze', icon: '🔔' },
      { path: '/documenti', label: 'Documenti', icon: '📄' },
      { path: '/detrazioni', label: 'Detrazioni', icon: '🏷️' },
    ]
  }
];

export function MainLayout({ children }: MainLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { projects, currentProject, setCurrentProjectId, isLoading, error } = useProjectContext();
  const createProjectMutation = useCreateProject();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAiSettings, setShowAiSettings] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Creating project:', newProjectName);
    if (!newProjectName.trim()) return;

    try {
      const project = await createProjectMutation.mutateAsync({ name: newProjectName.trim() });
      console.log('Project created:', project);
      setCurrentProjectId(project.id);
      setShowCreateModal(false);
      setNewProjectName('');
    } catch (err) {
      console.error('Create project error:', err);
      alert(`Errore: ${err instanceof Error ? err.message : 'Creazione fallita'}`);
    }
  };

  const handleProjectChange = (projectId: string) => {
    if (projectId === 'new') {
      setShowCreateModal(true);
    } else {
      setCurrentProjectId(projectId);
    }
  };

  const clearError = () => {
    // Errors are managed by react-query, no need to manually clear
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out z-30 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <Link to="/" className="text-xl font-bold text-primary-600 hover:text-primary-700">
              Home Renovation Guide
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Project Selector */}
          {projects.length > 0 && (
            <div className="px-4 py-3 border-b border-gray-200">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Progetto Attivo
              </label>
              <select
                value={currentProject?.id || ''}
                onChange={(e) => handleProjectChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                <option value="new">+ Nuovo Progetto</option>
              </select>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-6 overflow-y-auto">
            {navGroups.map((group) => (
              <div key={group.title}>
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.path ||
                      location.pathname.startsWith(item.path + '/') ||
                      (location.pathname === '/' && item.path === '/dashboard');
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User Info at bottom */}
          <div className="border-t border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 text-sm font-medium">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAiSettings(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
              Impostazioni AI
            </button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                {/* Mobile menu button */}
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                {/* Page title - shows current project on mobile */}
                <div className="lg:hidden">
                  {currentProject && (
                    <span className="text-sm font-medium text-gray-600">
                      {currentProject.name}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className="hidden sm:block text-sm text-gray-600">{user?.email}</span>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Esci
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-4 py-3">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <p className="text-sm text-red-700">{error instanceof Error ? error.message : 'Errore nel caricamento'}</p>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700"
              >
                x
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏠</div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Nessun progetto
              </h2>
              <p className="text-gray-600 mb-6">
                Crea il tuo primo progetto per iniziare a gestire le spese della casa.
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                Crea Progetto
              </Button>
            </div>
          ) : (
            children
          )}
        </main>

        {/* Save Indicator */}
        <SaveIndicator />
      </div>

      {/* AI Settings Modal */}
      <AiSettingsModal
        isOpen={showAiSettings}
        onClose={() => setShowAiSettings(false)}
      />

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setNewProjectName('');
        }}
        title="Nuovo Progetto"
      >
        <form onSubmit={handleCreateProject}>
          <Input
            label="Nome del progetto"
            type="text"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            placeholder="es. Casa Via Roma"
            autoFocus
          />
          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setNewProjectName('');
              }}
              disabled={createProjectMutation.isPending}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={createProjectMutation.isPending || !newProjectName.trim()}
            >
              {createProjectMutation.isPending ? 'Creazione...' : 'Crea'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
